import type { TFunction } from 'i18next';
import {
    isCatalogImportCode,
    isCatalogImportKeptReason,
    type CatalogImportKeptReason,
} from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    CatalogImportKeptType,
    CatalogImportUndoResult,
} from '@/api/models';
import {
    countParam,
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';

/**
 * Why the last Undo press did not undo the import. Pure data, so the dialog
 * only renders it:
 *
 * - `refused`: the server said why in a code of the import, one sentence
 *   (`IMPORT_NOT_LATEST`, `IMPORT_ALREADY_UNDONE`, `IMPORT_CONSUMED`,
 *   `ONBOARDING_FINISHED`, `IMPORT_NOT_FOUND`). Pressing again would get the
 *   same answer, so the dialog offers only Close.
 * - `failed`: no answer, or a code the import does not own (the books'):
 *   the dialog offers Try again.
 */
export type UndoFailure =
    { status: 'refused'; notice: CatalogImportNotice } | { status: 'failed' };

/** Reads the undo's rejection; see {@link UndoFailure}. */
export const undoFailureOf = (error: ErrorResponse | null): UndoFailure =>
    error?.code !== undefined && isCatalogImportCode(error.code)
        ? { status: 'refused', notice: noticeFor({ code: error.code }) }
        : { status: 'failed' };

// Literal keys, so the locale guards see every one of them.
export const KEPT_TYPE_LABEL_KEY = {
    Brand: 'catalogImport.result.undo.kept.type.brand',
    Category: 'catalogImport.result.undo.kept.type.category',
    CategoryAttribute: 'catalogImport.result.undo.kept.type.categoryAttribute',
    Supplier: 'catalogImport.result.undo.kept.type.supplier',
    AttributeValue: 'catalogImport.result.undo.kept.type.attributeValue',
} as const satisfies Record<CatalogImportKeptType, string>;

const KEPT_TYPE_UNKNOWN_KEY = 'catalogImport.result.undo.kept.type.other';

export const KEPT_REASON_LABEL_KEY = {
    USED_BY_PRODUCTS: 'catalogImport.result.undo.kept.reason.USED_BY_PRODUCTS',
    HAS_SUBCATEGORIES:
        'catalogImport.result.undo.kept.reason.HAS_SUBCATEGORIES',
    USED_BY_STOCK: 'catalogImport.result.undo.kept.reason.USED_BY_STOCK',
    USED_BY_PURCHASES:
        'catalogImport.result.undo.kept.reason.USED_BY_PURCHASES',
    USED_BY_PAYMENTS: 'catalogImport.result.undo.kept.reason.USED_BY_PAYMENTS',
    USED_BY_VARIANTS: 'catalogImport.result.undo.kept.reason.USED_BY_VARIANTS',
} as const satisfies Record<CatalogImportKeptReason, string>;

const KEPT_REASON_UNKNOWN_KEY = 'catalogImport.result.undo.kept.reason.other';

/** How many kept names the toast spells out before "and N more". */
export const UNDONE_KEPT_SHOWN = 5;

/** What the toast says after an undo. */
export type UndoneMessage = {
    title: string;
    /** What went: the products and stock, and the book entry when one was reversed. */
    summary: string;
    /** What stayed and why, one line; `null` when the undo kept nothing. */
    kept: string | null;
};

/**
 * The toast's sentences for a completed undo. The record names the branch
 * and whether a book entry was reversed (`reversalEntryId`); `kept[]` names
 * what something outside the import still uses, each with its kind and why,
 * the first {@link UNDONE_KEPT_SHOWN} spelled out. A kind or a reason this
 * build does not know reads generically rather than as a raw value.
 *
 * Resolves its keys under `pages` whichever namespace `t` is bound to, as
 * `renderNotice` does.
 */
export const undoneMessageOf = (
    t: TFunction,
    { branchName, reversalEntryId, kept }: CatalogImportUndoResult,
): UndoneMessage => {
    const shown = kept.slice(0, UNDONE_KEPT_SHOWN).map((item) =>
        t('catalogImport.result.undo.kept.item', {
            ns: 'pages',
            name: item.name,
            // The wire's kinds are an enum the server may grow.
            type: t(
                Object.hasOwn(KEPT_TYPE_LABEL_KEY, item.entityType)
                    ? KEPT_TYPE_LABEL_KEY[item.entityType]
                    : KEPT_TYPE_UNKNOWN_KEY,
                { ns: 'pages' },
            ),
            reason: t(
                isCatalogImportKeptReason(item.reason)
                    ? KEPT_REASON_LABEL_KEY[item.reason]
                    : KEPT_REASON_UNKNOWN_KEY,
                { ns: 'pages' },
            ),
        }),
    );
    const more = kept.length - shown.length;
    const items = shown.join(', ');

    return {
        title: t('catalogImport.result.undo.done.title', { ns: 'pages' }),
        summary:
            reversalEntryId === null
                ? t('catalogImport.result.undo.done.summary', {
                      ns: 'pages',
                      branch: branchName,
                  })
                : t('catalogImport.result.undo.done.summaryReversed', {
                      ns: 'pages',
                      branch: branchName,
                  }),
        kept:
            kept.length === 0
                ? null
                : more > 0
                  ? t('catalogImport.result.undo.kept.lineMore', {
                        ns: 'pages',
                        items,
                        more: countParam(more),
                    })
                  : t('catalogImport.result.undo.kept.line', {
                        ns: 'pages',
                        items,
                    }),
    };
};
